import { useMemo } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import { useChartAxisApi } from '@aytm/app/stats/page/question-views/components/charts/highcharts/hooks/useChartAxisApi'

import {
  getChartModeTypes,
  getDefaultChartMode
} from '@aytm/app/stats/page/question-views/sh/components/chart/lib/chartModes'
import { getTraitValues } from '@aytm/app/stats/page/question-views/sh/components/chart/lib/traitValues'
import { getAnswer } from '@aytm/app/stats/page/question-views/sh/components/chart/lib/hcAnswers'
import { getBatches } from '@aytm/app/stats/page/question-views/sh/components/chart/lib/batches'
import { getYAxisTitle, getYAxisLimits } from '@aytm/app/stats/page/question-views/sh/components/chart/lib/yAxis'

import ChartContainer from '@aytm/app/stats/page/question-views/components/charts/highcharts/ChartContainer'
import { calcIgnorePercents } from '@aytm/app/stats/page/question-views/components/lib/chart-modes'

const ShelfTestChart = ({
  questionId,
  questionKey,
  qType,
  answerVariants,
  answer,
  sortOrder,
  chartMode: chartModeProp,
  globalChartMode,
  modelChartMode,
  onChartModeChange,
  zoomLevel,
  globalZoomLevel,
  onZoomChange,
  isTraitType,
  traitName,
  trait,
  ignorePercents: ignoresPercents = false,
  format,
  viewMode,
  priceTemplate,
  colorTheme,
  chartLegendMode,
  confidenceLevel,
  significanceLevel,
  confidenceLevels,
  features,
  onChartReady
}) => {
  const chartRef = useChartAxisApi(onChartReady)

  const chartModeTypes = useMemo(() => getChartModeTypes({ answerVariants, viewMode }), [answerVariants, viewMode])

  const chartMode = useMemo(
    () =>
      getDefaultChartMode({
        chartMode: chartModeProp,
        globalChartMode,
        modelChartMode,
        answerVariants,
        viewMode
      }),
    [chartModeProp, globalChartMode, modelChartMode, answerVariants, viewMode]
  )

  const traitValues = useMemo(() => getTraitValues(trait), [trait])

  const hcAnswers = useMemo(
    () => getAnswer({ answer, answerVariants, viewMode, priceTemplate, trait, traitName, traitValues }),
    [answer, answerVariants, viewMode, priceTemplate, trait, traitName, traitValues]
  )

  const batches = useMemo(
    () => (viewMode === 'totalPickedUp' ? getBatches({ answerVariants, trait, traitValues }) : null),
    [viewMode, answerVariants, trait, traitValues]
  )

  const yAxisTitle = useMemo(() => getYAxisTitle({ viewMode, chartMode }), [viewMode, chartMode])
  // Split by trait plots one bar per trait value; limits taken from the ungrouped amounts would
  // clip the series actually drawn, so the axis is left to fit itself.
  const yAxisLimits = useMemo(
    () => (trait ? false : getYAxisLimits({ viewMode, answerVariants, hcAnswers })),
    [viewMode, answerVariants, hcAnswers, trait]
  )

  const registryChartMode = viewMode === 'totalPickedUp' ? `stacked_${chartMode}` : chartMode

  const ignoreTraits = !isTraitType
  // the shares the chart leaves out: the question's own, and every view but Amount, which has none
  const ignorePercents = calcIgnorePercents(format, ignoresPercents) || viewMode !== 'amount'
  const viewOptionsDisabled = features?.view_options?.disabled

  return (
    <ChartContainer
      chartRef={chartRef}
      questionId={questionId}
      questionKey={questionKey}
      qType={qType}
      answerVariants={viewMode === 'totalPickedUp' ? undefined : answerVariants}
      batches={batches}
      answer={hcAnswers}
      sortOrder={sortOrder}
      chartMode={registryChartMode}
      selectorChartModeValue={chartMode}
      chartModeOverride={chartModeProp}
      globalChartMode={globalChartMode}
      chartModeTypes={chartModeTypes}
      viewOptionsDisabled={viewOptionsDisabled}
      onChartModeChange={onChartModeChange}
      zoomLevel={zoomLevel}
      globalZoomLevel={globalZoomLevel}
      zoomType={viewMode !== 'totalPickedUp'}
      onZoomChange={onZoomChange}
      traitName={traitName}
      trait={trait}
      traitValues={traitValues}
      ignoreTraits={ignoreTraits}
      ignorePercents={ignorePercents}
      viewMode={viewMode}
      yAxisTitle={yAxisTitle}
      yAxisLimits={yAxisLimits || undefined}
      colorTheme={colorTheme}
      chartLegendMode={chartLegendMode}
      confidenceLevel={confidenceLevel}
      significanceLevel={significanceLevel}
      confidenceLevels={confidenceLevels}
      features={features}
    />
  )
}

ShelfTestChart.propTypes = {
  questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  questionKey: PropTypes.string,
  qType: PropTypes.string,
  answerVariants: PropTypes.array,
  answer: answerPropType,
  sortOrder: PropTypes.string,
  chartMode: PropTypes.string,
  globalChartMode: PropTypes.string,
  modelChartMode: PropTypes.string,
  onChartModeChange: PropTypes.func,
  zoomLevel: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.array]),
  globalZoomLevel: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onZoomChange: PropTypes.func,
  isTraitType: PropTypes.bool,
  traitName: PropTypes.string,
  trait: PropTypes.object,
  ignorePercents: PropTypes.bool,
  format: PropTypes.object,
  viewMode: PropTypes.string,
  priceTemplate: PropTypes.string,
  colorTheme: PropTypes.array,
  chartLegendMode: PropTypes.string,
  confidenceLevel: PropTypes.number,
  significanceLevel: PropTypes.number,
  confidenceLevels: PropTypes.array,
  features: PropTypes.object,
  onChartReady: PropTypes.func
}

ShelfTestChart.displayName = 'ShelfTestChart'

export default ShelfTestChart
